import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion, useScroll, useTransform, type Variants } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Check,
  Crown,
  Database,
  Eye,
  Globe,
  Layers,
  Lock,
  LogIn,
  Menu,
  MousePointerClick,
  Play,
  RefreshCw,
  Repeat,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Workflow,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { PlatformAnalytics } from '../components/Common/PlatformAnalytics';
import { JsonLd } from '../components/Common/JsonLd';
import {
  PLANS as PRICING_PLANS,
  USD_TO_THB_RATE,
  effectiveMonthlyUsd,
  formatThb,
  formatUsd,
  usdToThb,
  type Cycle,
  type PlanId,
  type Tier,
} from '../constants/pricing';

type PlanFeature = {
  text: string;
  included: boolean;
};

/** Marketing-page pricing card.
 *  - `tier === 'free'` has no purchasable plan id; the CTA goes to /register.
 *  - All paid tiers send the user to /upgrade where the full feature matrix
 *    + checkout flow lives — we deliberately don't duplicate Omise wiring
 *    on the public marketing page. */
type Plan = {
  name: string;
  tier: Tier;
  description: string;
  badge?: string;
  features: PlanFeature[];
  buttonText: string;
  popular: boolean;
};

type FaqItem = {
  question: string;
  answer: string;
};

type Feature = {
  icon: LucideIcon;
  title: string;
  kicker: string;
  desc: string;
  proof: string;
  gradient: string;
};

type Platform = {
  name: string;
  category: string;
  logo: React.ReactNode;
};

const easeOut = [0.16, 1, 0.3, 1] as const;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 34 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.64, ease: easeOut },
  }),
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (i: number = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.06, duration: 0.54, ease: easeOut },
  }),
};

function CountUp({ end, suffix = '' }: { end: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node || shouldReduceMotion) {
      setCount(end);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        let frame = 0;
        const totalFrames = 48;
        const animate = () => {
          frame += 1;
          const progress = Math.min(frame / totalFrames, 1);
          setCount(Math.round(end * progress));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      },
      { threshold: 0.45 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [end, shouldReduceMotion]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

function LogoMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${compact ? 'h-8 w-8 rounded-xl' : 'h-10 w-10 rounded-2xl'} relative grid place-items-center overflow-hidden bg-gradient-to-br from-lime-300 via-cyan-300 to-violet-500 shadow-glow`}>
      <div className="absolute inset-0 bg-growth-ink/10" />
      <Layers className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} relative text-growth-ink`} />
    </div>
  );
}

/** Fixed bottom-left scroll-percentage readout. Lives in its own
 *  component with a private, rAF-throttled scroll listener that writes
 *  the value straight into a ref'd DOM node — so updating it on every
 *  scroll frame never re-renders the parent landing page (which is a
 *  ~2k-line tree and would otherwise stutter the whole scroll). */
function ScrollProgressHUD() {
  const valueRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let ticking = false;
    let last = -1;
    const update = () => {
      ticking = false;
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const pct = max > 0 ? Math.min(100, Math.round((window.scrollY / max) * 100)) : 0;
      if (pct === last) return;
      last = pct;
      if (valueRef.current) valueRef.current.textContent = String(pct).padStart(2, '0');
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="pointer-events-none fixed bottom-5 left-5 z-50 hidden items-center gap-2 rounded-full border border-white/10 bg-growth-ink/90 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-white/70 sm:flex">
      <span className="h-1.5 w-1.5 animate-glow-pulse rounded-full bg-lime-300" />
      <span ref={valueRef}>00</span>%
    </div>
  );
}

// Desktop section dot-nav. Owns the active-section IntersectionObserver
// and toggles `data-active` directly on the dot DOM nodes — keeping this
// out of the giant Home tree means crossing a section boundary never
// re-renders the whole landing page mid-scroll (a big jank source).
function SectionDotNav() {
  const shouldReduceMotion = useReducedMotion();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = navRef.current;
    if (!root) return undefined;
    const observed = snapSections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (observed.length === 0) return undefined;

    const setActive = (id: string) => {
      root.querySelectorAll<HTMLButtonElement>('button[data-section]').forEach((btn) => {
        const on = btn.dataset.section === id;
        btn.dataset.active = on ? 'true' : 'false';
        if (on) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target.id) setActive(visible.target.id);
      },
      { threshold: [0.35, 0.6], rootMargin: '-20% 0px -35% 0px' },
    );

    observed.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <nav ref={navRef} className="landing-dotnav" aria-label="Section navigation">
      {snapSections.map((section, idx) => (
        <button
          key={section.id}
          type="button"
          data-section={section.id}
          data-active={idx === 0 ? 'true' : 'false'}
          onClick={() =>
            document
              .getElementById(section.id)
              ?.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth' })
          }
          aria-label={section.label}
          aria-current={idx === 0 ? 'true' : undefined}
          title={section.label}
        />
      ))}
    </nav>
  );
}

function BrowserFrame({
  children,
  url = 'app.hitzlink.com',
  className = '',
}: {
  children: React.ReactNode;
  url?: string;
  className?: string;
}) {
  return (
    <div className={`glass-skin overflow-hidden rounded-[1.75rem] bg-slate-950/60 ${className}`}>
      <div className="flex items-center gap-3 border-b border-white/10 bg-white/[0.04] px-4 py-3">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <div className="mx-auto flex max-w-xs items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] font-medium text-white/[0.55]">
          <Lock className="h-3 w-3 text-lime-300" />
          {url}
        </div>
      </div>
      {children}
    </div>
  );
}

/** Auto-crossfading stack of REAL product screenshots shown inside a
 *  BrowserFrame. Cycles every few seconds (held static for
 *  reduced-motion), with a moving gloss sweep, a "Live" pulse badge and
 *  slide dots — so the captures feel like a living product surface
 *  rather than a flat paste. All frames share one fixed aspect box so
 *  swapping images never reflows the layout. */
function FrameShots({
  images,
  accentDot = 'bg-lime-300',
}: {
  images: Array<{ src: string; alt: string }>;
  accentDot?: string;
}) {
  const shouldReduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion || images.length < 2) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % images.length);
    }, 4200);
    return () => clearInterval(id);
  }, [shouldReduceMotion, images.length]);

  return (
    <div className="relative aspect-[1024/520] w-full overflow-hidden bg-[#080a15]">
      {images.map((img, idx) => (
        <motion.img
          key={img.src}
          src={img.src}
          alt={img.alt}
          className="absolute inset-0 h-full w-full object-contain object-top"
          initial={false}
          animate={{ opacity: idx === active ? 1 : 0, scale: idx === active ? 1 : 1.02 }}
          transition={{ duration: 0.8, ease: easeOut }}
          loading="lazy"
          decoding="async"
        />
      ))}

      {/* moving gloss sweep */}
      {!shouldReduceMotion && (
        <motion.div
          className="pointer-events-none absolute inset-y-0 -left-1/4 w-1/4 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent"
          animate={{ x: ['0%', '520%'] }}
          transition={{ duration: 6.5, ease: 'easeInOut', repeat: Infinity, repeatDelay: 2.2 }}
        />
      )}

      {/* live badge */}
      <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/45 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
        <span className="relative flex h-1.5 w-1.5">
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${accentDot} opacity-75`} />
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${accentDot}`} />
        </span>
        Live · ของจริงจากระบบ
      </div>

      {/* slide dots */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {images.map((img, idx) => (
            <button
              key={img.src}
              type="button"
              aria-label={`ดูภาพตัวอย่างที่ ${idx + 1}`}
              onClick={() => setActive(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === active ? `w-5 ${accentDot}` : 'w-1.5 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Hero showcase
   ────────────────────────────────────────────────────────────────
   Two authentic surfaces composed together so the hero shows BOTH
   halves of the product story:

   1. A real screen capture of the in-app Analytics dashboard
      (public/screenshots/analytics-dashboard.png) framed inside a
      macOS BrowserFrame — i.e. "the real product UI you get".
   2. A mobile link-in-bio landing-page mock (gradient, avatar,
      verified, link buttons, social row) floating over it — i.e.
      "what you build & publish". */

/** Mobile link-in-bio landing page mock — the meelink.net-style page a
 *  user builds & publishes on HitzLink. Floats over the analytics frame
 *  on large screens. */
function HeroPhoneMock({ className = '' }: { className?: string }) {
  return (
    <div className={`w-[176px] rounded-[2.2rem] border border-white/15 bg-slate-950 p-2 shadow-2xl ${className}`}>
      <div className="relative overflow-hidden rounded-[1.7rem] bg-gradient-to-b from-violet-500 via-fuchsia-500 to-rose-400">
        {/* notch */}
        <div className="absolute left-1/2 top-2 z-10 h-4 w-16 -translate-x-1/2 rounded-full bg-black/30" />
        <div className="absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/20 blur-2xl" />
        <div className="relative px-4 pb-4 pt-8 text-center text-white">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-white/90 to-white/60 text-lg font-black text-fuchsia-600 ring-2 ring-white/70">
            S
          </div>
          <div className="mt-2 flex items-center justify-center gap-1">
            <span className="text-[13px] font-black">@summerbrand</span>
            <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-sky-400">
              <Check className="h-2.5 w-2.5 text-white" />
            </span>
          </div>
          <div className="mt-0.5 text-[10px] font-medium text-white/80">ลิงก์รวมโปรโมชั่น 🔥</div>

          <div className="mt-3 space-y-2">
            {['⚡ Flash Sale 48 ชม.', '🎁 รับโค้ดส่วนลด', '💬 ทักแชทสั่งซื้อ'].map((label) => (
              <div
                key={label}
                className="rounded-full border border-white/30 bg-white/20 px-3 py-2 text-[11px] font-bold text-white backdrop-blur-sm"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-center gap-2 [&_svg]:h-4 [&_svg]:w-4">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90">{PlatformLogos.instagram}</span>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90">{PlatformLogos.tiktok}</span>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90">{PlatformLogos.youtube}</span>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90">{PlatformLogos.line}</span>
          </div>

          <div className="mt-3 text-[8px] font-semibold uppercase tracking-[0.18em] text-white/70">
            hitzlink.com/summerbrand
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroShowcase() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-[2.2rem] bg-gradient-to-br from-lime-300/30 via-cyan-300/20 to-violet-500/35 blur-3xl animate-glow-pulse" />

      {/* Real product UI — actual screen capture of the in-app Analytics dashboard */}
      <BrowserFrame className="landing-glow-ring relative animate-float-slow" url="app.hitzlink.com/analytics">
        <img
          src="/screenshots/analytics-dashboard.png"
          alt="HitzLink Analytics dashboard — real-time views, unique visitors, conversion rate, top landing pages and traffic-trend chart"
          className="block w-full"
          loading="lazy"
          decoding="async"
        />
      </BrowserFrame>

      {/* "What you build" — link-in-bio phone, overlapping on large screens */}
      <HeroPhoneMock className="animate-float-slow absolute -bottom-10 -left-10 z-20 hidden lg:block [animation-delay:1.2s]" />

      {/* compact phone for small screens, inline below the frame */}
      <div className="mt-6 flex justify-center lg:hidden">
        <HeroPhoneMock />
      </div>
    </div>
  );
}

const PlatformLogos = {
  facebook: (
    <svg className="h-6 w-6" fill="#1877F2" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  meta: (
    <svg className="h-6 w-6" fill="#0064E0" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 3.205 1.18 5.52 4.439 5.52 1.497 0 2.633-.67 3.965-2.443.76-1.012 1.144-1.626 2.663-4.32l.942-1.664.183.3 2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 2.82 0 4.126-2.13 4.126-5.56 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.699 0-3.32 1.24-4.873 3.358C10.525 5.141 8.8 4.03 6.915 4.03Zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028c-.45-.75-.87-1.404-1.255-1.98 1.28-1.94 2.34-2.929 3.568-2.929Zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-1.04 0-1.847-.904-1.847-2.84 0-2.221.63-4.535 1.66-6.088.764-1.157 1.625-1.818 2.621-1.818Z" />
    </svg>
  ),
  googleAnalytics: (
    <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F9AB00" d="M22.84 2.998v17.999a2.983 2.983 0 0 1-5.967.092V3.066a2.985 2.985 0 0 1 2.62-2.96 2.983 2.983 0 0 1 3.347 2.892Z" />
      <path fill="#E37400" d="M4.117 17.997a2.983 2.983 0 1 1 0 5.966 2.983 2.983 0 0 1 0-5.966Zm7.882-9.014a3.057 3.057 0 0 0-3.052 3.144v8.121a2.999 2.999 0 0 0 5.971.34V12.13a3 3 0 0 0-2.92-3.147Z" />
    </svg>
  ),
  googleTagManager: (
    <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#8AB4F8" d="M12.001 24c-2.08 0-3.776-1.565-3.776-3.491 0-1.927 1.695-3.492 3.776-3.492 2.082 0 3.776 1.565 3.776 3.492S14.083 24 12 24Z" />
      <path fill="#4285F4" d="m7.454 14.436-7.025-7.025a1.675 1.675 0 0 1 0-2.37L4.952.542a1.675 1.675 0 0 1 2.371 0l13.061 13.061c.465.464.468 1.219.004 1.684l-4.517 4.517a1.071 1.071 0 0 1-1.515 0l-6.902-5.368Z" />
      <path fill="#246FDB" d="M23.509 9.225 14.781.5l-3.013 3.014 8.727 8.727Z" />
    </svg>
  ),
  tiktok: (
    <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#25F4EE" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 11.14-4.02V9.13a8.16 8.16 0 0 0 4.65 1.46V7.18a4.85 4.85 0 0 1-1.2-.49Z" />
      <path fill="#FE2C55" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 11.14-4.02V9.13a8.16 8.16 0 0 0 4.65 1.46V7.18a4.85 4.85 0 0 1-1.2-.49Z" opacity="0.72" />
    </svg>
  ),
  instagram: (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#E1306C" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92C2.174 15.584 2.163 15.205 2.163 12c0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069Zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947C23.732 2.699 21.311.273 16.949.073 15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.325 6.162 6.162 0 0 0 0-12.325Zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z" />
    </svg>
  ),
  youtube: (
    <svg className="h-6 w-6" fill="#FF0000" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" />
    </svg>
  ),
  telegram: (
    <svg className="h-6 w-6" fill="#2AABEE" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.056Zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635Z" />
    </svg>
  ),
  line: (
    <svg className="h-6 w-6" fill="#06C755" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755Zm-3.855 3.016c0 .27-.174.51-.432.596a.628.628 0 0 1-.709-.219l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595a.607.607 0 0 1 .689.221l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771Zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771Zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  ),
};

/** Text monogram fallback for trackers that don't ship an SVG mark.
 *  Keeps the marquee/grid visually consistent with the real logos. */
function TrackerMark({ label, className }: { label: string; className: string }) {
  return (
    <span className={`grid h-6 w-6 place-items-center rounded-md text-[11px] font-black text-white ${className}`}>
      {label}
    </span>
  );
}

const platforms: Platform[] = [
  { name: 'Meta Ads', category: 'Ad platform', logo: PlatformLogos.meta },
  { name: 'Facebook Pixel', category: 'Conversion pixel', logo: PlatformLogos.facebook },
  { name: 'Google Analytics', category: 'Analytics', logo: PlatformLogos.googleAnalytics },
  { name: 'Google Tag Manager', category: 'Tag management', logo: PlatformLogos.googleTagManager },
  { name: 'TikTok Pixel', category: 'Conversion pixel', logo: PlatformLogos.tiktok },
  { name: 'Instagram', category: 'Social traffic', logo: PlatformLogos.instagram },
  { name: 'YouTube', category: 'Video traffic', logo: PlatformLogos.youtube },
  { name: 'Telegram', category: 'Messaging', logo: PlatformLogos.telegram },
  { name: 'LINE', category: 'Messaging', logo: PlatformLogos.line },
  { name: 'Voluum', category: 'Affiliate tracker', logo: <TrackerMark label="V" className="bg-[#6c5ce7]" /> },
  { name: 'RedTrack', category: 'Affiliate tracker', logo: <TrackerMark label="R" className="bg-[#e74c3c]" /> },
  { name: 'Binom', category: 'Affiliate tracker', logo: <TrackerMark label="B" className="bg-[#0ea5e9]" /> },
];

const stats = [
  { labelKey: 'hero.stats.campaigns', value: 4_800, suffix: '+' },
  { labelKey: 'hero.stats.events', value: 12, suffix: 'M+' },
  { labelKey: 'hero.stats.connections', value: 9, suffix: '+' },
];

/** Core strengths shown as the pillar grid. Cloaking leads the list
 *  per the redesign brief; every card carries a plain-language `desc`
 *  so visitors understand the benefit, not just the feature name. */
const pillars: Feature[] = [
  {
    icon: Shield,
    title: 'Cloaking & Traffic Quality',
    kicker: 'ปกป้องแคมเปญ',
    desc: 'ระบบหลบบอท: คนตรวจโฆษณากับบอทจะเห็นหน้าธรรมดาๆ ส่วนลูกค้าจริงเห็นหน้าขายของจริง ช่วยลดโดนแบนและให้แคมเปญรันต่อได้ยาวๆ.',
    proof: 'พระเอกของ HitzLink — กรองคนดูจริงให้อัตโนมัติ',
    gradient: 'from-lime-300 to-emerald-400',
  },
  {
    icon: Server,
    title: 'Conversion API (CAPI)',
    kicker: 'ส่งยอดขายให้แม่น',
    desc: 'ส่งข้อมูลยอดขายตรงจากเซิร์ฟเวอร์เข้า Meta ข้อมูลไม่หายแม้เบราว์เซอร์หรือ ad blocker บล็อก ตัดยอดซ้ำให้อัตโนมัติ รองรับหลาย Pixel/Token พร้อมกัน.',
    proof: 'ส่งตรงจากเซิร์ฟเวอร์ ข้อมูลแม่น ไม่นับซ้ำ',
    gradient: 'from-cyan-300 to-blue-400',
  },
  {
    icon: RefreshCw,
    title: 'Pixel โดนแบน? ย้ายฟรี ไม่เสียข้อมูล',
    kicker: 'ข้อมูลยอดขายไม่มีวันหาย',
    desc: 'เพราะยอดขายถูกเก็บไว้ที่เซิร์ฟเวอร์ของเรา พอ Pixel หรือบัญชีโฆษณาโดนแบน ก็แค่เปิดตัวใหม่แล้วส่งยอดย้อนหลังกลับเข้าไปได้ฟรี ไม่ต้องเริ่มเก็บข้อมูลใหม่จากศูนย์.',
    proof: 'เปิด Pixel ใหม่ ส่งยอดย้อนหลังกลับได้ทันที',
    gradient: 'from-pink-400 to-rose-400',
  },
  {
    icon: Workflow,
    title: 'GTM Auto-Sync',
    kicker: 'เชื่อมครั้งเดียวจบ',
    desc: 'แค่ล็อกอิน Google เชื่อม Google Tag Manager ครั้งเดียว ระบบจะสร้างแท็กให้อัตโนมัติทั้งหมด ไม่ต้องมานั่งตั้งแท็กเองทีละตัว.',
    proof: 'ระบบสร้างแท็กให้ — คุณแค่กดเผยแพร่เอง',
    gradient: 'from-violet-400 to-pink-400',
  },
  {
    icon: Repeat,
    title: 'Tracking Postback (S2S)',
    kicker: 'ต่อ tracker ได้หมด',
    desc: 'ส่งยอดขายกลับเข้า tracker ยอดนิยม (Voluum / RedTrack / Binom / FunnelFlux / BeMob) ด้วยค่าตั้งสำเร็จรูป ดึงค่าคอมกลับมาดูใน HitzLink ได้.',
    proof: 'มีค่าตั้งสำเร็จรูปให้ tracker ดังๆ',
    gradient: 'from-amber-300 to-orange-400',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Visitor Tracking',
    kicker: 'รู้ว่าควรดันงบตรงไหน',
    desc: 'ดูยอดคนเข้า ยอดขาย อุปกรณ์ ประเทศ และพฤติกรรมรายคน เช่น อยู่ในหน้านานแค่ไหน รวมข้อมูลสำคัญไว้ในแดชบอร์ดเดียวอ่านง่าย.',
    proof: 'เห็นเส้นทางลูกค้าชัดตั้งแต่คลิกแรกถึงปิดการขาย',
    gradient: 'from-sky-300 to-cyan-400',
  },
  {
    icon: Globe,
    title: 'Domains & Short Links',
    kicker: 'ดูน่าเชื่อถือ',
    desc: 'ใช้โดเมนของคุณเองและสร้างลิงก์สั้นแบบมีแบรนด์ ทำให้ลิงก์ดูน่าเชื่อถือตั้งแต่ก่อนลูกค้ากดเข้า เหมาะกับการยิงหลายแคมเปญพร้อมกัน.',
    proof: 'โดเมนของคุณ + ลิงก์สั้น ครบในที่เดียว',
    gradient: 'from-emerald-300 to-teal-400',
  },
  {
    icon: Users,
    title: 'Team Management',
    kicker: 'ทำงานเป็นทีม',
    desc: 'ชวนทีมเข้ามาทำงานในที่เดียวกัน กำหนดได้ว่าใครแก้ได้ (Editor) ใครดูได้อย่างเดียว (Viewer) แยกบัญชีย่อยตามจำนวนที่นั่ง เหมาะกับเอเจนซีหลายลูกค้า.',
    proof: 'สิทธิ์ชัด ใครเห็น/แก้อะไรได้ คุมได้',
    gradient: 'from-indigo-300 to-violet-400',
  },
];

/** Deep-dive "spotlight" rows. Each renders an alternating text + glass
 *  dashboard mock; `accent` drives the per-row color theme and `mock`
 *  feeds a generic dashboard visualization so we get visual variety
 *  without hand-building seven bespoke mockups. */
type SpotlightAccent = 'lime' | 'cyan' | 'violet' | 'amber' | 'emerald' | 'pink' | 'sky';

type Spotlight = {
  id: string;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  accent: SpotlightAccent;
  mock: {
    url: string;
    tag: string;
    headline: string;
    rows: Array<{ label: string; value: string; pct: number }>;
    /** Real product screenshots. When present, the spotlight frame shows
     *  these actual captures (auto-crossfading) instead of the synthetic
     *  headline/rows mock. */
    images?: Array<{ src: string; alt: string }>;
  };
};

const spotlights: Spotlight[] = [
  {
    id: 'spotlight-cloaking',
    icon: Shield,
    eyebrow: 'Spotlight • พระเอก',
    title: 'Cloaking ระบบหลบบอท ที่ทำให้แคมเปญอยู่รอด',
    desc: 'ทุกครั้งที่มีคนเข้าหน้า ระบบจะเช็กว่าเป็นลูกค้าจริง หรือเป็นบอท/ทีมตรวจโฆษณา/เครื่องมือสอดส่อง แล้วเลือกหน้าให้เหมาะ — ฝั่งที่มาตรวจจะเห็นหน้าธรรมดาๆ ที่ดูเป็นเว็บปกติ ส่วนลูกค้าจริงเห็นหน้าขายของจริง ช่วยลดความเสี่ยงโดนแบนและทำให้แคมเปญรันต่อได้ยาวขึ้น (เราขอบอกแค่ประโยชน์ ไม่เปิดกลไกข้างใน).',
    bullets: [
      'แยกคนจริง / บอท / คนตรวจโฆษณา ได้แบบเรียลไทม์',
      'สลับเป็นหน้าธรรมดาให้อัตโนมัติเมื่อเจอคนมาตรวจ',
      'ตั้งเงื่อนไขได้ตามประเทศ อุปกรณ์ และที่มาของคนเข้า',
    ],
    accent: 'lime',
    mock: {
      url: 'app.hitzlink.com/cloaking',
      tag: 'Protected',
      headline: 'Traffic routing',
      rows: [
        { label: 'Real customers → Offer', value: '82%', pct: 82 },
        { label: 'Bots → Safe page', value: '14%', pct: 14 },
        { label: 'Reviewers → Safe page', value: '4%', pct: 8 },
      ],
      images: [
        {
          src: '/screenshots/cloaking-bots.png',
          alt: 'แดชบอร์ด Bot Cloaking จริง — บอทที่ถูกบล็อกแยกตามช่องทาง เช่น Meta/Facebook, Suspicious Request, TLS-mismatch และ Datacenter ASN',
        },
        {
          src: '/screenshots/cloaking-geo.png',
          alt: 'แดชบอร์ด Bot Cloaking จริง — การบล็อกแบบ geo-fenced ตามประเทศ (US, IN, SG, NL, IE)',
        },
      ],
    },
  },
  {
    id: 'spotlight-aisafe',
    icon: Sparkles,
    eyebrow: 'Spotlight • AI',
    title: 'AI Safe Page สร้างหน้าธรรมดาให้อัตโนมัติ',
    desc: 'ไม่ต้องมานั่งทำหน้าธรรมดาเอง — ให้ AI สร้างหน้าที่ดูเป็นเว็บจริง มีบทความ รูปภาพ และโครงสร้างที่สมเหตุสมผล เอาไว้โชว์ให้ฝั่งที่มาตรวจเห็นคู่กับระบบหลบบอท ประหยัดเวลาจากหลายชั่วโมงเหลือแค่ไม่กี่คลิก.',
    bullets: [
      'สร้างหน้าธรรมดาพร้อมเนื้อหาครบในไม่กี่คลิก',
      'ปรับธีม/ภาษา ให้เข้ากับแนวของแคมเปญได้',
      'ใช้คู่กับระบบหลบบอทได้ทันที',
    ],
    accent: 'violet',
    mock: {
      url: 'app.hitzlink.com/ai-safe-pages',
      tag: 'Generated',
      headline: 'AI safe page',
      rows: [
        { label: 'Content sections', value: '8', pct: 90 },
        { label: 'Generate time', value: '~30s', pct: 70 },
        { label: 'Quota / month', value: '20–100', pct: 60 },
      ],
    },
  },
  {
    id: 'spotlight-tracking',
    icon: Database,
    eyebrow: 'Spotlight • Data',
    title: 'เก็บข้อมูลยอดขายครบและแม่น',
    desc: 'รวมทุกอย่างที่สายยิงแอดต้องใช้วัดผล: ส่งยอดขายตรงจากเซิร์ฟเวอร์ (Meta CAPI) พร้อมตัดยอดซ้ำให้เอง, จัดการได้หลาย Pixel ในที่เดียว, ส่งข้อมูลเข้า tracker ได้ทุกเจ้า และดูได้ว่าใครอยู่หน้าไหนนานแค่ไหน มาจากอุปกรณ์/ประเทศไหน.',
    bullets: [
      'ส่งยอดขายตรงจากเซิร์ฟเวอร์ + ตัดยอดซ้ำให้เอง',
      'จัดการได้หลาย Pixel/Token ในที่เดียว',
      'ดูพฤติกรรมรายคน: เวลาในหน้า อุปกรณ์ ประเทศ',
    ],
    accent: 'cyan',
    mock: {
      url: 'app.hitzlink.com/analytics',
      tag: 'Live',
      headline: 'Conversion events',
      rows: [
        { label: 'CAPI events (server)', value: '12.4K', pct: 88 },
        { label: 'Deduped duplicates', value: '−3.1K', pct: 40 },
        { label: 'Match quality', value: 'High', pct: 76 },
      ],
      images: [
        {
          src: '/screenshots/analytics-trend-devices.png',
          alt: 'แดชบอร์ด Analytics จริง — กราฟแนวโน้ม วิว/ผู้เข้าชม/คอนเวอร์ชั่น, สัดส่วนอุปกรณ์ และทราฟฟิกรายชั่วโมง',
        },
        {
          src: '/screenshots/analytics-cities.png',
          alt: 'แดชบอร์ด Analytics จริง — เมืองยอดนิยมของผู้เข้าชม พร้อมสัดส่วนเปอร์เซ็นต์',
        },
        {
          src: '/screenshots/analytics-trend.png',
          alt: 'แดชบอร์ด Analytics จริง — กราฟแนวโน้มวิว ผู้เข้าชมไม่ซ้ำ และคอนเวอร์ชั่นรายวัน',
        },
      ],
    },
  },
  {
    id: 'spotlight-gtm',
    icon: Workflow,
    eyebrow: 'Spotlight • Automation',
    title: 'GTM Auto-Sync เชื่อมครั้งเดียว วางแท็กให้เอง',
    desc: 'แทนที่จะเปิด Google Tag Manager มานั่งสร้างแท็กและทริกเกอร์ทีละตัว แค่ล็อกอิน Google เชื่อมครั้งเดียว HitzLink จะสร้างแท็ก, ทริกเกอร์ และตัวแปรที่ต้องใช้ให้คุณอัตโนมัติ — คุณแค่ตรวจแล้วกดเผยแพร่เอง ปลอดภัยและคุมได้เต็มที่.',
    bullets: [
      'เชื่อม GTM ด้วยการล็อกอิน Google ไม่ต้องก๊อปโค้ดเอง',
      'ระบบสร้างแท็ก/ทริกเกอร์ให้อัตโนมัติทั้งหมด',
      'คุณเป็นคนกดเผยแพร่เองทุกครั้ง — คุมเวอร์ชันได้',
    ],
    accent: 'sky',
    mock: {
      url: 'app.hitzlink.com/gtm',
      tag: 'Synced',
      headline: 'GTM container',
      rows: [
        { label: 'Tags provisioned', value: '6', pct: 80 },
        { label: 'Triggers created', value: '9', pct: 70 },
        { label: 'dataLayer vars', value: '12', pct: 64 },
      ],
    },
  },
  {
    id: 'spotlight-ban',
    icon: RefreshCw,
    eyebrow: 'Spotlight • Recovery',
    title: 'Pixel โดนแบน? ย้ายฟรี ไม่เสียข้อมูล',
    desc: 'เวลาบัญชีหรือ Pixel โดนแบน คนอื่นต้องเริ่มเก็บข้อมูลใหม่จากศูนย์ แต่ HitzLink เก็บข้อมูลยอดขายไว้ที่ฝั่งเซิร์ฟเวอร์ให้ พอคุณเปิด Pixel ใหม่ ก็ส่งข้อมูลย้อนหลังกลับเข้าไปได้ พร้อมดึงรายชื่อลูกค้าออกไปทำกลุ่มเป้าหมาย (Custom/Lookalike) ใหม่บน Meta ได้ทันที',
    bullets: [
      'เก็บข้อมูลที่ฝั่งเซิร์ฟเวอร์ — แบนแล้วข้อมูลไม่หาย',
      'ส่งข้อมูลย้อนหลังกลับเข้า Pixel ใหม่ได้',
      'ดึงรายชื่อลูกค้าไปทำกลุ่มเป้าหมายบน Meta',
    ],
    accent: 'amber',
    mock: {
      url: 'app.hitzlink.com/recovery',
      tag: 'Recovered',
      headline: 'Ban recovery',
      rows: [
        { label: 'Events retained', value: '100%', pct: 100 },
        { label: 'Replayed to new token', value: '9.8K', pct: 84 },
        { label: 'Audience exported', value: 'Ready', pct: 72 },
      ],
    },
  },
  {
    id: 'spotlight-team',
    icon: Users,
    eyebrow: 'Spotlight • Team',
    title: 'Team Management สำหรับเอเจนซีและทีมโต',
    desc: 'ทำงานคนเดียวไม่พอแล้ว — ชวนทีมเข้ามาช่วยกันในที่เดียว กำหนดได้ว่าใครเป็น Editor (แก้แคมเปญได้) หรือ Viewer (ดูผลอย่างเดียว) และแยกบัญชีย่อยตามจำนวนที่นั่งของแพ็กเกจ เหมาะกับเอเจนซีที่ดูแลหลายลูกค้า หรือทีมที่มีคนยิงแอดหลายคนทำพร้อมกัน.',
    bullets: [
      'ชวนทีม / แยกบัญชีย่อย ทำงานในที่เดียวกัน',
      'สิทธิ์ Editor / Viewer คุมได้ว่าใครแก้อะไรได้',
      'เหมาะกับเอเจนซีหลายลูกค้า / ทีมที่มีหลายคนยิง',
    ],
    accent: 'emerald',
    mock: {
      url: 'app.hitzlink.com/team',
      tag: 'Workspace',
      headline: 'Team & roles',
      rows: [
        { label: 'Owner', value: 'Full access', pct: 100 },
        { label: 'Editor seats', value: '3', pct: 60 },
        { label: 'Viewer seats', value: '5', pct: 80 },
      ],
    },
  },
  {
    id: 'spotlight-builder',
    icon: MousePointerClick,
    eyebrow: 'Spotlight • Builder',
    title: 'No-code Builder ที่ออกแบบมาเพื่อปิดการขาย',
    desc: 'ต่อหน้าแคมเปญเองได้เหมือนต่อเลโก้ เลือกบล็อก ปรับข้อความ วางปุ่ม แล้วดูตัวอย่างบนมือถือได้ทันที มีบล็อกสายยิงแอด/เกมให้พร้อม ทั้ง Jackpot, SpinWheel, GameRTP, Leaderboard, Recent Withdrawals และ Online Users ที่หาไม่ได้ในตัวสร้างเว็บทั่วไป.',
    bullets: [
      'บล็อกพร้อมใช้กว่า 25 ชนิด + ดูตัวอย่างบนมือถือ',
      'บล็อกสายเกม: Jackpot, SpinWheel, Leaderboard',
      'แก้ข้อความ / ปุ่ม / รีวิว ได้เอง ไม่ต้องรอโปรแกรมเมอร์',
    ],
    accent: 'pink',
    mock: {
      url: 'app.hitzlink.com/builder',
      tag: 'Editing',
      headline: 'Page blocks',
      rows: [
        { label: 'Blocks available', value: '25+', pct: 92 },
        { label: 'Mobile preview', value: 'Live', pct: 80 },
        { label: 'iGaming blocks', value: '6', pct: 56 },
      ],
    },
  },
];

/** Per-accent Tailwind class bundles for the spotlight rows + their
 *  glass mock dashboards. Keeps the JSX free of long ternary chains. */
const SPOTLIGHT_ACCENT: Record<SpotlightAccent, { chip: string; icon: string; glow: string; bar: string; value: string }> = {
  lime: { chip: 'bg-lime-300/15 text-lime-200', icon: 'from-lime-300 to-emerald-400', glow: 'bg-lime-300/20', bar: 'bg-lime-300', value: 'text-lime-200' },
  cyan: { chip: 'bg-cyan-300/15 text-cyan-200', icon: 'from-cyan-300 to-blue-400', glow: 'bg-cyan-300/20', bar: 'bg-cyan-300', value: 'text-cyan-200' },
  violet: { chip: 'bg-violet-400/15 text-violet-200', icon: 'from-violet-400 to-pink-400', glow: 'bg-violet-400/20', bar: 'bg-violet-300', value: 'text-violet-200' },
  amber: { chip: 'bg-amber-300/15 text-amber-200', icon: 'from-amber-300 to-orange-400', glow: 'bg-amber-300/20', bar: 'bg-amber-300', value: 'text-amber-200' },
  emerald: { chip: 'bg-emerald-300/15 text-emerald-200', icon: 'from-emerald-300 to-teal-400', glow: 'bg-emerald-300/20', bar: 'bg-emerald-300', value: 'text-emerald-200' },
  pink: { chip: 'bg-pink-400/15 text-pink-200', icon: 'from-pink-400 to-rose-400', glow: 'bg-pink-400/20', bar: 'bg-pink-300', value: 'text-pink-200' },
  sky: { chip: 'bg-sky-300/15 text-sky-200', icon: 'from-sky-300 to-cyan-400', glow: 'bg-sky-300/20', bar: 'bg-sky-300', value: 'text-sky-200' },
};

/** "Everything included" capability matrix — grouped checklist that
 *  shows breadth at a glance, with a tier hint per group. */
const capabilityGroups: Array<{ icon: LucideIcon; title: string; tier: string; items: string[] }> = [
  {
    icon: ShieldCheck,
    title: 'Protection & Traffic',
    tier: 'Agency+',
    items: ['Cloaking engine', 'Advanced traffic quality', 'AI Safe Pages', 'Device redirect + Geo-fence'],
  },
  {
    icon: Server,
    title: 'Tracking & Data',
    tier: 'ทุกแผน*',
    items: ['Meta CAPI + dedup', 'Pixel / Token manager', 'Affiliate postback (S2S)', 'Visitor & session tracking'],
  },
  {
    icon: Workflow,
    title: 'Automation & Integrations',
    tier: 'Agency+',
    items: ['เชื่อม GTM สร้างแท็กให้อัตโนมัติ', 'กู้ข้อมูลกลับหลังโดนแบน', 'ดึงรายชื่อลูกค้าส่งเข้า Meta', 'ค่าตั้งสำเร็จรูปสำหรับ Voluum/RedTrack/Binom'],
  },
  {
    icon: Users,
    title: 'Build & Collaborate',
    tier: 'ทุกแผน*',
    items: ['No-code builder 25+ บล็อก', 'Custom domains + short links', 'Team & sub-account roles', 'Analytics dashboard'],
  },
];

const plans: Plan[] = [
  {
    name: 'Free',
    tier: 'free',
    description: 'เหมาะสำหรับทดลองสร้าง landing page แรก',
    features: [
      { text: '1 Landing Page', included: true },
      { text: 'Builder + Component ครบ 25+ ชนิด', included: true },
      { text: 'Facebook Pixel (1 ตัว) + Pixel Manager', included: true },
      { text: 'GA4 / GTM (manual) / TikTok Pixel', included: true },
      { text: 'เก็บข้อมูลสถิติย้อนหลัง 7 วัน', included: true },
      { text: 'Meta CAPI (Server-side)', included: false },
      { text: 'ระบบหลบบอท (Bot Cloaking)', included: false },
      { text: 'Custom Domain', included: false },
    ],
    buttonText: 'เริ่มต้นฟรี',
    popular: false,
  },
  {
    name: 'Pro',
    tier: 'pro',
    description: 'สำหรับนักยิงแอด — โดเมนส่วนตัว + Postback + ทีมขนาดเล็ก',
    features: [
      { text: '5 Landing Pages + 3 Custom Domains', included: true },
      { text: 'Facebook Pixel (3 ตัว) + Pixel Manager', included: true },
      { text: 'Affiliate S2S Postback + Tracker Presets', included: true },
      { text: 'Device Redirect + Geo-fence (รายประเทศ)', included: true },
      { text: 'สมาชิกในทีม 2 ที่นั่ง + ปิดป้ายแบรนด์อัตโนมัติ', included: true },
      { text: 'เก็บข้อมูลสถิติ 90 วัน + CSV Export', included: true },
      { text: 'Meta CAPI (Server-side)', included: false },
      { text: 'ระบบหลบบอท (Bot Cloaking) + AI Safe Pages', included: false },
    ],
    buttonText: 'อัปเกรดเป็น Pro',
    popular: false,
  },
  {
    name: 'Agency',
    tier: 'agency',
    description: 'ครบเครื่องเรื่องระบบหลบบอท + AI Safe Pages + Meta CAPI',
    badge: 'Most Popular',
    features: [
      { text: '20 Landing Pages + 15 Custom Domains', included: true },
      { text: 'Facebook Pixels ไม่จำกัด + Pixel สำรองกันแบน', included: true },
      { text: 'ระบบหลบบอท (Bot Cloaking Engine) + Bot Logs', included: true },
      { text: 'AI Safe Pages — 20 ครั้ง / เดือน', included: true },
      { text: 'Multi-Token Meta CAPI + Live Health Dashboard', included: true },
      { text: 'Audience Export → Meta Customer File', included: true },
      { text: 'สมาชิกในทีม 5 ที่นั่ง + Custom CSS', included: true },
    ],
    buttonText: 'อัปเกรดเป็น Agency',
    popular: true,
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    description: 'ใช้งานไม่จำกัด พร้อมซิงก์ Pixel เก่า (CAPI Replay) และ GTM อัตโนมัติ',
    features: [
      { text: 'Landing Pages & Custom Domains ไม่จำกัด', included: true },
      { text: 'Facebook Pixels & สมาชิกในทีม ไม่จำกัด', included: true },
      { text: 'CAPI Replay (ซิงก์อีเวนต์จาก Pixel เก่าไป Pixel ใหม่)', included: true },
      { text: 'GTM API Auto-Provisioning (OAuth)', included: true },
      { text: 'AI Safe Pages — 100 ครั้ง / เดือน', included: true },
      { text: 'White-label เต็มรูปแบบ + พื้นที่ 500 GB', included: true },
      { text: 'ผู้ดูแลส่วนตัว (Dedicated Support SLA 2 ชม.)', included: true },
    ],
    buttonText: 'อัปเกรดเป็น Enterprise',
    popular: false,
  },
];

const fallbackFaqs: FaqItem[] = [
  {
    question: 'HitzLink เหมาะกับใคร?',
    answer: 'เหมาะกับคนที่ยิงโฆษณาเป็นหลัก ทั้งสาย affiliate และทีมการตลาด ที่ต้องสร้าง Landing Page หลายแคมเปญ อยากให้ข้อมูลยอดขายแม่น เชื่อม GTM ง่ายๆ และมีระบบหลบบอทกันโดนแบน ครบในเว็บเดียว.',
  },
  {
    question: 'ทำไมต้องเลือก HitzLink แทนเครื่องมือทั่วไป?',
    answer: 'เพราะ HitzLink รวมตัวสร้าง Landing Page, ระบบหลบบอท, ส่งข้อมูลยอดขายให้แม่น, เชื่อม GTM อัตโนมัติ, หน้า Safe จาก AI และการกู้ข้อมูลหลังโดนแบนไว้ในที่เดียว — แทนที่จะต้องใช้ Voluum / RedTrack / ตัวหลบบอท / ตัวสร้างเว็บแยกกัน แล้วมานั่งต่อกันเอง.',
  },
  {
    question: 'ทำไมราคาเป็น USD?',
    answer: 'เพราะคนสายยิงแอดส่วนใหญ่คิดราคาเป็น USD เพื่อเทียบกับเครื่องมือต่างประเทศได้ตรงๆ เราเลยแสดงราคาเป็น USD แต่จ่ายจริงเป็นเงินบาทผ่าน Omise (PromptPay / TrueMoney / LINE Pay / SCB / KBank) ที่เรตคงที่ $1 = ฿35.',
  },
  {
    question: 'ระบบหลบบอท (Advanced Traffic Quality) คืออะไร?',
    answer: 'เป็นชุดความสามารถที่คอยกรองคนเข้าหน้า แยกคนจริงออกจากบอท แล้วสลับเป็นหน้าธรรมดาให้อัตโนมัติเมื่อเจอทีมตรวจโฆษณา เราขอบอกแค่ประโยชน์ ไม่เปิดเผยกลไกข้างในบนหน้าเว็บสาธารณะ.',
  },
  {
    question: 'รองรับแพลตฟอร์มอะไรบ้าง?',
    answer: 'รองรับ Meta/Facebook Pixel + Conversions API, TikTok Pixel, Google Analytics, Google Tag Manager (เชื่อมแล้วสร้างแท็กให้อัตโนมัติ), ส่งข้อมูลเข้า tracker ของ affiliate และมีค่าตั้งสำเร็จรูปสำหรับ Voluum / RedTrack / Binom (Agency ขึ้นไป).',
  },
];

const navItems = [
  { id: 'features', labelKey: 'nav.features' },
  { id: 'spotlights', labelKey: 'nav.spotlights' },
  { id: 'integrations', labelKey: 'nav.integrations' },
  { id: 'pricing', labelKey: 'nav.pricing' },
  { id: 'faq', labelKey: 'nav.faq' },
];

/** Ordered list of full-bleed sections used for the section-by-section
 *  snap experience + the right-side dot navigation. Each id maps to a
 *  `data-snap` <section> in the markup; `label` shows on hover. */
const snapSections: Array<{ id: string; label: string }> = [
  { id: 'hero', label: 'Hero' },
  { id: 'why', label: 'Why HitzLink' },
  { id: 'features', label: 'Core strengths' },
  { id: 'spotlights', label: 'Deep dive' },
  { id: 'metrics', label: 'Results' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'matrix', label: 'Everything included' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
  { id: 'cta', label: 'Get started' },
];

function handleCardPointerMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--card-x', `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty('--card-y', `${e.clientY - rect.top}px`);
}

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const { isAuthenticated, user } = useAuthStore();
  const { settings } = usePlatformSettings();
  const shouldReduceMotion = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  const [heroTilt, setHeroTilt] = useState({ x: 0, y: 0 });
  const [spotlight, setSpotlight] = useState({ x: 50, y: 16 });
  const [billingCycle, setBillingCycle] = useState<Cycle>('monthly');
  // Mobile nav drawer state — below md the in-bar nav items are
  // hidden, so we surface them as a slide-down panel triggered by
  // a hamburger button.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  // Enable section-by-section scroll snapping only while the landing
  // page is mounted (and only when the visitor hasn't asked for
  // reduced motion). The class is read by `html.home-snap` rules in
  // index.css; removing it on unmount keeps app/admin routes normal.
  useEffect(() => {
    if (shouldReduceMotion) return undefined;
    const root = document.documentElement;
    root.classList.add('home-snap');
    return () => root.classList.remove('home-snap');
  }, [shouldReduceMotion]);

  // Lock body scroll while the mobile menu is open so the page
  // doesn't scroll behind the overlay.
  useEffect(() => {
    if (mobileMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [mobileMenuOpen]);

  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const heroY = useTransform(scrollYProgress, [0, 0.35], [0, shouldReduceMotion ? 0 : 90]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.32], [1, shouldReduceMotion ? 1 : 0.35]);

  // Only flips a boolean for the top-nav shadow. rAF-throttled and
  // guarded so we don't re-render this (very large) component on every
  // scroll frame — the scroll-percentage HUD lives in its own isolated
  // component so the page tree stays untouched while scrolling.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        setScrolled((prev) => {
          const next = window.scrollY > 20;
          return prev === next ? prev : next;
        });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleGetStarted = useCallback(() => {
    navigate(isAuthenticated ? '/dashboard' : '/register');
  }, [isAuthenticated, navigate]);

  /** Send a paid-tier card click to /upgrade with the chosen plan id
   *  pre-selected via the query string. /upgrade is the canonical place
   *  for the full feature matrix + Omise checkout; we don't duplicate
   *  that wiring on the marketing page. Logged-out users hit /upgrade
   *  too — the page itself nudges them through the auth gate. */
  const handleSelectPaidTier = useCallback(
    (tier: Exclude<Tier, 'free'>) => {
      const planId: PlanId = `${tier}_${billingCycle}` as PlanId;
      navigate(`/upgrade?plan=${planId}`);
    },
    [billingCycle, navigate],
  );

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth' });
  }, [shouldReduceMotion]);

  const handleHeroPointerMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const x = (localX / rect.width - 0.5) * 2;
    const y = (localY / rect.height - 0.5) * 2;

    setSpotlight({
      x: Math.round((localX / rect.width) * 100),
      y: Math.round((localY / rect.height) * 100),
    });

    if (!shouldReduceMotion) {
      setHeroTilt({ x: y * -4, y: x * 5 });
    }
  }, [shouldReduceMotion]);

  const resetHeroTilt = useCallback(() => {
    setHeroTilt({ x: 0, y: 0 });
  }, []);

  const brandName = settings.logo_text || settings.site_name || 'HitzLink';
  const logoAlt = settings.logo_text || settings.site_name || 'Logo';
  const localizedNavItems = navItems.map((item) => ({ ...item, label: t(item.labelKey) }));
  const localizedStats = stats.map((stat) => ({ ...stat, label: t(stat.labelKey) }));
  const localizedPlans = plans.map((plan) => ({
    ...plan,
    description: t(`pricing.plans.${plan.tier}.description`),
    badge: plan.badge ? t(`pricing.plans.${plan.tier}.badge`) : undefined,
    buttonText: t(`pricing.plans.${plan.tier}.button`),
  }));
  const translatedFaqs = t('faq.items', { returnObjects: true });
  const faqItems: FaqItem[] = Array.isArray(translatedFaqs) ? translatedFaqs as FaqItem[] : fallbackFaqs;

  return (
    <>
      <Helmet>
        <title>{settings.seo_title || 'HitzLink - Growth Landing Page Platform'}</title>
        <meta
          name="description"
          content={settings.seo_description || 'Build high-converting landing pages, connect tracking pixels, and optimize campaign performance with HitzLink.'}
        />
        {settings.seo_keywords && <meta name="keywords" content={settings.seo_keywords} />}
        {settings.favicon_url && <link rel="icon" href={`${settings.favicon_url}?v=${Date.now()}`} />}
        {settings.favicon_url && <link rel="shortcut icon" href={`${settings.favicon_url}?v=${Date.now()}`} />}
        {settings.favicon_url && <link rel="apple-touch-icon" href={`${settings.favicon_url}?v=${Date.now()}`} />}
        <meta property="og:title" content={settings.seo_title || 'HitzLink - Growth Landing Page Platform'} />
        <meta property="og:description" content={settings.seo_description || 'Launch, track, and optimize campaign landing pages faster.'} />
        {settings.seo_og_image && <meta property="og:image" content={settings.seo_og_image} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={settings.seo_title || 'HitzLink'} />
        <meta name="twitter:description" content={settings.seo_description || 'Launch, track, and optimize campaign landing pages faster.'} />
        {settings.seo_og_image && <meta name="twitter:image" content={settings.seo_og_image} />}
      </Helmet>

      <PlatformAnalytics
        googleAnalyticsId={settings.google_analytics_id}
        googleTagManagerId={settings.google_tag_manager_id}
        facebookPixelId={settings.facebook_pixel_id}
        tiktokPixelId={settings.tiktok_pixel_id}
      />

      {/* SoftwareApplication schema — feeds Google's product
          knowledge panel and helps AI assistants summarize what
          HitzLink is when prompted "what is hitzlink.com". The
          publisher reference is resolved against the Organization
          @id emitted by PlatformHead. */}
      <JsonLd
        id="software-application"
        data={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          '@id': 'https://hitzlink.com#software',
          name: 'HitzLink',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          url: 'https://hitzlink.com',
          description:
            settings.seo_description ||
            'Build high-converting landing pages, connect tracking pixels, and optimize campaign performance.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'THB',
            availability: 'https://schema.org/InStock',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            reviewCount: '128',
          },
          publisher: { '@id': 'https://hitzlink.com#organization' },
        }}
      />

      {/* FAQPage schema — pulled straight from the FAQ items
          that the page already renders. The acceptedAnswer.text
          is plain text (no HTML) because schema.org explicitly
          permits both but Google's parser is more forgiving with
          plain text and we don't need formatting in the snippet. */}
      {faqItems.length > 0 && (
        <JsonLd
          id="faqpage"
          data={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((f) => ({
              '@type': 'Question',
              name: f.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: f.answer,
              },
            })),
          }}
        />
      )}

      <div
        className="landing-shell min-h-screen overflow-x-hidden text-slate-950"
        style={{
          '--spotlight-x': `${spotlight.x}%`,
          '--spotlight-y': `${spotlight.y}%`,
        } as React.CSSProperties}
      >
        <motion.div className="fixed left-0 top-0 z-[60] h-1 origin-left bg-gradient-to-r from-lime-300 via-cyan-300 to-violet-400" style={{ scaleX }} />

        {/* Animated aurora + tech-grid backdrop. Fixed + pointer-events
            none so it never interferes with layout or scroll snapping.
            Only the dark upper band shows it; the white sections below
            simply paint over it. */}
        {!shouldReduceMotion && (
          <div className="landing-aurora" aria-hidden="true">
            <div className="landing-aurora__blob animate-aurora left-[-10%] top-[-8%] h-[34rem] w-[34rem] bg-cyan-400/30" />
            <div className="landing-aurora__blob animate-aurora-slow right-[-12%] top-[2%] h-[40rem] w-[40rem] bg-violet-500/30" />
            <div className="landing-aurora__blob animate-aurora left-[20%] top-[18%] h-[26rem] w-[26rem] bg-lime-300/15" style={{ animationDelay: '-6s' }} />
            <div className="landing-aurora__blob animate-aurora-slow left-[45%] top-[6%] h-[30rem] w-[30rem] bg-pink-500/12" style={{ animationDelay: '-10s' }} />
          </div>
        )}

        {/* Scroll-progress HUD — bottom-left counter, echoing the
            example site's "0%" readout. Isolated into its own component
            so its per-scroll state updates never re-render the whole
            (very large) landing tree. */}
        <ScrollProgressHUD />

        {/* Section dot navigation (desktop) — isolated so its active-state
            updates never re-render this (very large) landing tree. */}
        <SectionDotNav />

        <motion.nav
          className={`fixed top-0 z-50 w-full transition-all duration-500 ${
            scrolled
              ? 'border-b border-white/10 bg-growth-ink/95 shadow-[0_18px_70px_rgba(0,0,0,0.24)]'
              : 'bg-growth-ink/40'
          }`}
          initial={{ y: -82 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.65, ease: easeOut }}
        >
          <div className="landing-section">
            <div className="flex h-[4.5rem] items-center justify-between py-4">
              <button type="button" onClick={() => scrollTo('top')} className="flex items-center gap-3 text-left">
                {settings.logo_url ? (
                  <img
                    src={`${settings.logo_url}?v=${Date.now()}`}
                    alt={logoAlt}
                    width={36}
                    height={36}
                    /* Explicit width/height reserves layout space and
                       prevents the nav bar from jumping when the logo
                       resolves — kills the most visible CLS event
                       Lighthouse flags on Home. fetchpriority="high"
                       tells the browser to prioritize this image,
                       since on most viewports the logo competes with
                       the hero copy for the LCP slot. */
                    {...({ fetchpriority: 'high' } as { fetchpriority: 'high' })}
                    decoding="async"
                    className="h-9 w-auto object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <LogoMark compact />
                )}
                <span className="text-lg font-black tracking-tight text-white">{brandName}</span>
              </button>

              <div className="glass-skin hidden items-center gap-7 rounded-full px-5 py-2.5 md:flex">
                {localizedNavItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollTo(item.id)}
                    className="text-sm font-semibold text-white/[0.62] transition-colors hover:text-white"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                {isAuthenticated ? (
                  <>
                    <button type="button" onClick={() => navigate('/dashboard')} className="hidden rounded-xl px-4 py-2 text-sm font-bold text-white/70 transition hover:text-white sm:block">
                      {t('nav.dashboard')}
                    </button>
                    {user?.role === 'admin' && (
                      <button type="button" onClick={() => navigate('/admin')} className="hidden items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-cyan-200 transition hover:text-cyan-100 sm:flex">
                        <Shield className="h-3.5 w-3.5" />
                        {t('nav.admin')}
                      </button>
                    )}
                  </>
                ) : (
                  <button type="button" onClick={() => navigate('/login')} className="hidden items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-white/[0.65] transition hover:text-white sm:flex">
                    <LogIn className="h-3.5 w-3.5" />
                    {t('nav.login')}
                  </button>
                )}
                <button type="button" onClick={handleGetStarted} className="landing-primary-cta hidden sm:inline-flex px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm">
                  <span>{t('nav.getStarted')}</span>
                  <ArrowRight className="h-4 w-4" />
                </button>

                {/* Mobile hamburger — replaces the (hidden) horizontal
                    nav pill below the md breakpoint. Toggling it opens
                    a full-width slide-down panel with the same nav
                    items + the auth/CTA actions that don't fit in the
                    cramped mobile header bar. */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                  className="md:hidden inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/[0.06] p-2 text-white backdrop-blur-xl transition hover:bg-white/[0.12]"
                  aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile slide-down nav panel. Animated via CSS transform so
              we don't need to mount/unmount and lose focus. The panel
              lives INSIDE the motion.nav so it inherits the same
              backdrop-blur background and stays anchored to the top
              bar. The animated translate-y closes via CSS opacity +
              translate so users see the panel collapse rather than
              just disappear. */}
          <div
            className={`md:hidden overflow-hidden border-t border-white/10 bg-growth-ink/[0.98] transition-all duration-300 ease-out ${
              mobileMenuOpen ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
            }`}
          >
            <div className="landing-section py-5">
              <div className="flex flex-col gap-1">
                {localizedNavItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      // Defer scroll so the close animation can start
                      // before the page jumps — feels less jarring.
                      setTimeout(() => scrollTo(item.id), 100);
                    }}
                    className="w-full rounded-xl px-4 py-3 text-left text-base font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-2 border-t border-white/10 pt-4">
                {isAuthenticated ? (
                  <>
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-bold text-white/85 transition hover:bg-white/[0.12]"
                    >
                      {t('nav.dashboard')}
                    </button>
                    {user?.role === 'admin' && (
                      <button
                        type="button"
                        onClick={() => { setMobileMenuOpen(false); navigate('/admin'); }}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-300/30 bg-cyan-300/10 px-4 py-3 text-sm font-bold text-cyan-100 transition hover:bg-cyan-300/20"
                      >
                        <Shield className="h-4 w-4" />
                        {t('nav.admin')}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-bold text-white/85 transition hover:bg-white/[0.12]"
                  >
                    <LogIn className="h-4 w-4" />
                    {t('nav.login')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMobileMenuOpen(false); handleGetStarted(); }}
                  className="landing-primary-cta w-full justify-center"
                >
                  {t('nav.getStarted')}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.nav>

        <main id="top" className="relative z-10">
          <section
            id="hero"
            data-snap
            ref={heroRef}
            className="relative flex min-h-[100svh] items-center overflow-hidden pb-20 pt-32 text-white lg:pb-28 lg:pt-36"
            onMouseMove={handleHeroPointerMove}
            onMouseLeave={resetHeroTilt}
          >
            <div className="absolute left-1/2 top-24 h-72 w-72 -translate-x-1/2 rounded-full bg-lime-300/10 blur-[90px]" />
            <div className="absolute bottom-20 right-0 h-96 w-96 rounded-full bg-violet-500/15 blur-[110px]" />

            <motion.div className="landing-section relative z-10" style={{ y: heroY, opacity: heroOpacity }}>
              <div className="grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:gap-14">
                <div>
                  <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
                    <div className="landing-eyebrow">
                      <Sparkles className="h-3.5 w-3.5 text-lime-200" />
                      {t('hero.eyebrow')}
                    </div>
                  </motion.div>

                  <motion.h1
                    className="mt-7 max-w-4xl text-[2rem] font-black leading-[1.18] tracking-[-0.01em] text-white sm:text-4xl md:text-5xl lg:text-[3.75rem] lg:leading-[1.14]"
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                  >
                    {t('hero.headline')}{' '}
                    <span className="landing-gradient-text">{t('hero.headlineAccent')}</span>
                  </motion.h1>

                  <motion.p
                    className="mt-7 max-w-2xl text-lg leading-8 text-white/[0.68] md:text-xl"
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                  >
                    {t('hero.subtitle')}
                  </motion.p>

                  <motion.div
                    className="mt-9 flex flex-col gap-3 sm:flex-row"
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                  >
                    <button type="button" onClick={handleGetStarted} className="landing-primary-cta">
                      {t('hero.primaryCta')}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => scrollTo('features')} className="landing-secondary-cta">
                      <Play className="h-4 w-4" />
                      {t('hero.secondaryCta')}
                    </button>
                  </motion.div>

                  <motion.div
                    className="mt-10 grid max-w-2xl grid-cols-3 gap-3"
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={4}
                  >
                    {localizedStats.map((stat) => (
                      <div key={stat.label} className="glass-skin rounded-2xl p-4">
                        <div className="text-2xl font-black text-white md:text-3xl">
                          <CountUp end={stat.value} suffix={stat.suffix} />
                        </div>
                        <div className="mt-1 text-xs font-semibold text-white/[0.48]">{stat.label}</div>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 36, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.75, ease: easeOut }}
                  style={{
                    rotateX: shouldReduceMotion ? 0 : heroTilt.x,
                    rotateY: shouldReduceMotion ? 0 : heroTilt.y,
                    transformPerspective: 1000,
                  }}
                >
                  <HeroShowcase />
                </motion.div>
              </div>
            </motion.div>
          </section>

          <section id="integrations" data-snap className="relative -mt-6 pb-16 text-white">
            <div className="landing-section">
              <div className="landing-glass overflow-hidden rounded-[2rem] py-8">
                <div className="mb-6 px-6 text-center text-xs font-bold uppercase tracking-[0.24em] text-white/[0.5]">
                  {t('integrations.eyebrow')}
                </div>
                <div className="space-y-4 [mask-image:linear-gradient(to_right,transparent,#000_7%,#000_93%,transparent)]">
                  {/* Two rows drifting in opposite directions so the
                      ecosystem band reads as full / always-moving. */}
                  <div className="relative overflow-hidden">
                    <div className="flex w-max animate-logo-marquee gap-3 px-3">
                      {[...platforms, ...platforms].map((platform, index) => (
                        <div key={`row1-${platform.name}-${index}`} className="landing-logo-chip">
                          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-white">{platform.logo}</div>
                          <div>
                            <div className="text-sm font-black">{platform.name}</div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-white/[0.42]">{platform.category}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="relative overflow-hidden">
                    <div className="flex w-max animate-logo-marquee gap-3 px-3 [animation-direction:reverse]">
                      {[...platforms].reverse().concat([...platforms].reverse()).map((platform, index) => (
                        <div key={`row2-${platform.name}-${index}`} className="landing-logo-chip">
                          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-white">{platform.logo}</div>
                          <div>
                            <div className="text-sm font-black">{platform.name}</div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-white/[0.42]">{platform.category}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="why" data-snap className="relative overflow-hidden bg-growth-ink py-20 text-white lg:py-28">
            <div className="landing-noise absolute inset-0 opacity-20" />
            <div className="absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-violet-500/10 blur-[120px]" />
            <div className="landing-section relative">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <div className="landing-eyebrow mx-auto">
                  <Layers className="h-3.5 w-3.5 text-lime-200" />
                  ทำไมต้อง HitzLink
                </div>
                <h2 className="mt-6 text-4xl font-black tracking-[-0.045em] md:text-5xl lg:whitespace-nowrap">
                  เลิกต่อหลายเครื่องมือ <span className="landing-gradient-text">รวมจบในที่เดียว</span>
                </h2>
                <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/[0.62]">
                  สายยิงแอดส่วนใหญ่ต้องจ่ายค่าตัวสร้างเว็บ, ตัวเก็บข้อมูล, ระบบหลบบอท และตัวส่งข้อมูลยอดขายแยกกัน แล้วเสียเวลานั่งต่อให้คุยกันเอง — HitzLink รวมทุกอย่างไว้ในที่เดียวที่ออกแบบมาให้ทำงานด้วยกันตั้งแต่แรก.
                </p>
              </motion.div>

              <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-3">
                {[
                  { icon: Layers, title: 'ก่อนหน้านี้', body: 'ใช้เครื่องมือหลายตัวต่อกันเอง จ่ายหลายบิล ข้อมูลกระจัดกระจาย' },
                  { icon: ArrowRight, title: 'เปลี่ยนมาใช้', body: 'ย้ายงานทั้งหมดมาไว้บน HitzLink ที่เดียว ตั้งค่าทีเดียวใช้ได้ทั้งระบบ' },
                  { icon: Sparkles, title: 'ผลลัพธ์', body: 'เปิดแคมเปญได้เร็วขึ้น ข้อมูลแม่นขึ้น คุมแคมเปญและทีมได้จากที่เดียว' },
                ].map((step, index) => (
                  <motion.div
                    key={step.title}
                    className="glass-skin rounded-3xl p-6 text-left"
                    variants={scaleIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    custom={index}
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10">
                      <step.icon className="h-5 w-5 text-lime-200" />
                    </div>
                    <div className="mt-4 text-sm font-black uppercase tracking-[0.18em] text-white/[0.5]">{step.title}</div>
                    <p className="mt-2 text-sm leading-7 text-white/[0.74]">{step.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          <section id="features" data-snap className="bg-white py-20 lg:py-28">
            <div className="landing-section">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                  <Zap className="h-3.5 w-3.5 text-lime-500" />
                  Core strengths
                </div>
                <h2 className="landing-dark-gradient-text mt-5 text-4xl font-black tracking-[-0.04em] md:text-6xl">
                  8 ฟีเจอร์หลักที่สายยิงแอดต้องมี
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  ตั้งแต่ระบบหลบบอทกันแบน ส่งข้อมูลยอดขายให้แม่น เชื่อม GTM ไปจนถึงทำงานเป็นทีม — ทุกฟีเจอร์ออกแบบมาเพื่อคนยิงโฆษณาโดยเฉพาะ.
                </p>
              </motion.div>

              <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {pillars.map((feature, index) => (
                  <motion.article
                    key={feature.title}
                    className={`landing-card landing-card-spotlight group relative ${
                      index === 0 ? 'ring-2 ring-lime-300/70 ring-offset-2 ring-offset-white' : ''
                    }`}
                    variants={scaleIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    custom={index}
                    onMouseMove={handleCardPointerMove}
                  >
                    {index === 0 && (
                      <div className="absolute right-4 top-4 rounded-full bg-lime-300 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-growth-ink">
                        พระเอก
                      </div>
                    )}
                    <div className={`mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br ${feature.gradient} shadow-lg shadow-slate-900/10`}>
                      <feature.icon className="h-5 w-5 text-slate-950" />
                    </div>
                    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">{feature.kicker}</div>
                    <h3 className="mt-2 text-xl font-black tracking-tight text-slate-950">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600">{feature.desc}</p>
                    <div className="mt-5 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-lime-500" />
                      {feature.proof}
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          <section id="spotlights" data-snap className="relative overflow-hidden bg-growth-ink py-20 text-white lg:py-28">
            <div className="landing-noise absolute inset-0 opacity-20" />
            <div className="absolute right-[-10%] top-1/3 h-80 w-80 rounded-full bg-cyan-400/10 blur-[130px]" />
            <div className="landing-section relative">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <div className="landing-eyebrow mx-auto">
                  <Eye className="h-3.5 w-3.5 text-lime-200" />
                  ตัวอย่างจากระบบจริง
                </div>
                <h2 className="mt-6 text-4xl font-black tracking-[-0.045em] md:text-5xl lg:whitespace-nowrap">
                  ดูของจริงว่าแต่ละฟีเจอร์<span className="landing-gradient-text">ช่วยแคมเปญยังไง</span>
                </h2>
              </motion.div>

              <div className="mt-16 space-y-16 lg:space-y-24">
                {spotlights.map((item, index) => {
                  const accent = SPOTLIGHT_ACCENT[item.accent];
                  const flip = index % 2 === 1;
                  return (
                    <motion.div
                      key={item.id}
                      className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14"
                      variants={fadeUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.25 }}
                    >
                      <div className={flip ? 'lg:order-2' : ''}>
                        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.2em] ${accent.chip}`}>
                          <item.icon className="h-3.5 w-3.5" />
                          {item.eyebrow}
                        </div>
                        <h3 className="mt-5 text-3xl font-black tracking-[-0.035em] md:text-4xl">{item.title}</h3>
                        <p className="mt-5 text-base leading-8 text-white/[0.66]">{item.desc}</p>
                        <div className="mt-7 space-y-3">
                          {item.bullets.map((bullet) => (
                            <div key={bullet} className="flex items-start gap-3 text-sm font-semibold text-white/[0.82]">
                              <span className={`mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br ${accent.icon} text-growth-ink`}>
                                <Check className="h-3.5 w-3.5" />
                              </span>
                              {bullet}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={`relative ${flip ? 'lg:order-1' : ''}`}>
                        <div className={`absolute -inset-6 rounded-[2.2rem] blur-3xl ${accent.glow}`} />
                        {item.mock.images ? (
                          <BrowserFrame
                            className="landing-glow-ring relative animate-float-slow"
                            url={item.mock.url}
                          >
                            <FrameShots images={item.mock.images} accentDot={accent.bar} />
                          </BrowserFrame>
                        ) : (
                          <BrowserFrame className="relative" url={item.mock.url}>
                            <div className="bg-[#080a15] p-5">
                              <div className="mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br ${accent.icon}`}>
                                    <item.icon className="h-4 w-4 text-growth-ink" />
                                  </span>
                                  <div className="text-sm font-black text-white">{item.mock.headline}</div>
                                </div>
                                <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${accent.chip}`}>
                                  {item.mock.tag}
                                </span>
                              </div>
                              <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                                {item.mock.rows.map((row, rowIndex) => (
                                  <div key={row.label}>
                                    <div className="flex items-center justify-between text-[11px] font-bold">
                                      <span className="text-white/[0.6]">{row.label}</span>
                                      <span className={accent.value}>{row.value}</span>
                                    </div>
                                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/[0.08]">
                                      <motion.div
                                        className={`h-full rounded-full ${accent.bar}`}
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${row.pct}%` }}
                                        viewport={{ once: true }}
                                        transition={{ delay: rowIndex * 0.12, duration: 0.9, ease: easeOut }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </BrowserFrame>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          <section id="metrics" data-snap className="relative overflow-hidden bg-growth-ink py-20 text-white lg:py-28">
            <div className="landing-noise absolute inset-0 opacity-20" />
            <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-lime-300/10 blur-[120px]" />
            <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-violet-500/[0.15] blur-[120px]" />
            <div className="landing-section relative">
              <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <div className="landing-eyebrow">
                    <TrendingUp className="h-3.5 w-3.5 text-lime-200" />
                    Conversion narrative
                  </div>
                  <h2 className="mt-6 text-4xl font-black tracking-[-0.045em] md:text-6xl">
                    วัดผลได้ชัดขึ้น ปรับแคมเปญได้ไวขึ้น
                  </h2>
                  <p className="mt-5 text-lg leading-8 text-white/[0.62]">
                    หน้า landing ที่สวยอย่างเดียวไม่พอ ระบบต้องช่วยให้ทีมรู้ว่า traffic มาจากไหน คลิกตรงไหน และจุดไหนทำให้ conversion ดีขึ้น.
                  </p>
                </motion.div>

                <div className="grid gap-4 sm:grid-cols-3">
                  {[
                    { icon: TrendingUp, label: 'Conversion Rate', before: '1.8%', after: '4.2%', detail: 'จากการปรับ offer และ CTA' },
                    { icon: Target, label: 'Cost per Lead', before: '฿45', after: '฿18', detail: 'landing page ที่โฟกัส intent' },
                    { icon: Eye, label: 'Data Clarity', before: 'กระจัดกระจาย', after: 'อ่านง่าย', detail: 'รวม signal สำคัญใน dashboard' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      className="landing-glass rounded-3xl p-6"
                      variants={scaleIn}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.3 }}
                      custom={index}
                    >
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10">
                        <item.icon className="h-5 w-5 text-lime-200" />
                      </div>
                      <div className="mt-6 text-sm font-black text-white">{item.label}</div>
                      <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.05] p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/[0.38]">Before</div>
                        <div className="mt-1 text-xl font-black text-white/[0.58]">{item.before}</div>
                      </div>
                      <div className="mt-3 rounded-2xl bg-gradient-to-br from-lime-300 to-cyan-300 p-4 text-growth-ink">
                        <div className="text-[10px] font-black uppercase tracking-wider opacity-70">After HitzLink</div>
                        <div className="mt-1 text-2xl font-black">{item.after}</div>
                      </div>
                      <p className="mt-4 text-xs leading-6 text-white/[0.48]">{item.detail}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="platforms" data-snap className="bg-white py-20 lg:py-28">
            <div className="landing-section">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <h2 className="landing-dark-gradient-text text-4xl font-black tracking-[-0.04em] md:text-5xl lg:whitespace-nowrap">
                  เชื่อมต่อกับเครื่องมือที่สายยิงแอดใช้จริง
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  ต่อกับแพลตฟอร์มโฆษณา ตัววัดผล และ tracker ยอดนิยมได้ครบทุกขั้น ตั้งแต่ยิงแอด ส่งข้อมูลยอดขาย ไปจนถึงวัดผล — แถมเพิ่มแพลตฟอร์มใหม่ๆ ให้เรื่อยๆ.
                </p>
              </motion.div>

              <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {platforms.map((platform, index) => (
                  <motion.div
                    key={platform.name}
                    className="landing-card flex items-center gap-4 p-5"
                    variants={scaleIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    custom={index}
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                      {platform.logo}
                    </div>
                    <div>
                      <div className="font-black text-slate-950">{platform.name}</div>
                      <div className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{platform.category}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          <section id="matrix" data-snap className="relative overflow-hidden bg-growth-ink py-20 text-white lg:py-28">
            <div className="landing-noise absolute inset-0 opacity-20" />
            <div className="absolute left-[-8%] top-1/4 h-80 w-80 rounded-full bg-violet-500/10 blur-[130px]" />
            <div className="landing-section relative">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <div className="landing-eyebrow mx-auto">
                  <Check className="h-3.5 w-3.5 text-lime-200" />
                  ทุกอย่างอยู่ในที่เดียว
                </div>
                <h2 className="mt-6 text-4xl font-black tracking-[-0.045em] md:text-5xl lg:whitespace-nowrap">
                  ความสามารถทั้งหมด <span className="landing-gradient-text">รวมไว้ในที่เดียว</span>
                </h2>
                <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/[0.62]">
                  รวมทุกฟีเจอร์ของ HitzLink ไว้ให้ดูครบในที่เดียวว่าได้อะไรบ้าง — บางฟีเจอร์ปลดล็อกเพิ่มตามแพ็กเกจที่เลือก.
                </p>
              </motion.div>

              <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {capabilityGroups.map((group, index) => (
                  <motion.div
                    key={group.title}
                    className="glass-skin rounded-3xl p-6"
                    variants={scaleIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    custom={index}
                  >
                    <div className="flex items-center justify-between">
                      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10">
                        <group.icon className="h-5 w-5 text-lime-200" />
                      </div>
                      <span className="rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white/[0.6]">
                        {group.tier}
                      </span>
                    </div>
                    <h3 className="mt-5 text-lg font-black text-white">{group.title}</h3>
                    <ul className="mt-4 space-y-3">
                      {group.items.map((capItem) => (
                        <li key={capItem} className="flex items-start gap-2.5 text-sm font-semibold text-white/[0.78]">
                          <span className="mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-lime-300 text-growth-ink">
                            <Check className="h-3 w-3" />
                          </span>
                          {capItem}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
              <p className="mt-8 text-center text-xs font-semibold text-white/[0.4]">
                * ความพร้อมใช้งานบางรายการขึ้นกับแผน — ดูรายละเอียดเต็มที่ตารางราคาด้านล่าง
              </p>
            </div>
          </section>

          <section id="pricing" data-snap className="bg-slate-50 py-20 lg:py-28">
            <div className="landing-section">
              <motion.div
                className="mx-auto max-w-3xl text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                  <Crown className="h-3.5 w-3.5 text-amber-500" />
                  {t('pricing.eyebrow')}
                </div>
                <h2 className="landing-dark-gradient-text mt-5 text-4xl font-black tracking-[-0.04em] md:text-5xl lg:whitespace-nowrap">
                  {t('pricing.headline')}
                </h2>
                <p className="mt-5 text-lg text-slate-600">
                  {t('pricing.subtitle', { rate: USD_TO_THB_RATE })}
                </p>

                {/* Monthly / Yearly toggle — yearly already includes the 20%
                    discount baked into PLANS.yearly entries. */}
                <div className="mt-8 inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`rounded-full px-5 py-2 text-sm font-black transition-all ${
                      billingCycle === 'monthly' ? 'bg-slate-950 text-white shadow' : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {t('pricing.monthly')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-black transition-all ${
                      billingCycle === 'yearly' ? 'bg-slate-950 text-white shadow' : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {t('pricing.yearly')}
                    <span className="rounded-full bg-lime-300 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-growth-ink">
                      {t('pricing.yearlyBadge')}
                    </span>
                  </button>
                </div>
              </motion.div>

              <div className="mx-auto mt-12 grid max-w-7xl gap-6 md:grid-cols-2 xl:grid-cols-4">
                {localizedPlans.map((plan, index) => {
                  const planId: PlanId | null = plan.tier === 'free' ? null : (`${plan.tier}_${billingCycle}` as PlanId);
                  const pricing = planId ? PRICING_PLANS[planId] : null;
                  const headlineUsd = planId ? effectiveMonthlyUsd(planId) : 0;
                  const checkoutThb = pricing ? usdToThb(pricing.usd) : 0;

                  return (
                    <motion.article
                      key={plan.name}
                      className={`relative rounded-[2rem] border p-7 transition-all duration-300 hover:-translate-y-1 ${
                        plan.popular
                          ? 'border-slate-950 bg-slate-950 text-white shadow-premium-card'
                          : 'border-slate-200 bg-white text-slate-950 shadow-[0_24px_70px_rgba(15,23,42,0.07)]'
                      }`}
                      variants={scaleIn}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.25 }}
                      custom={index}
                    >
                      {plan.badge && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-lime-300 px-4 py-1.5 text-xs font-black uppercase tracking-wider text-growth-ink">
                          {plan.badge}
                        </div>
                      )}
                      <div>
                        <h3 className="text-2xl font-black">{plan.name}</h3>
                        <p className={`mt-2 text-sm leading-6 min-h-[2.75rem] ${plan.popular ? 'text-white/[0.55]' : 'text-slate-500'}`}>
                          {plan.description}
                        </p>
                      </div>

                      {/* Price block. Free shows "$0"; paid tiers show
                          USD as headline, then a small "billed annually"
                          line for yearly + the THB-at-checkout disclosure. */}
                      <div className="mt-7">
                        <div className="flex items-end gap-2">
                          <div className="text-5xl font-black tracking-tight">
                            {plan.tier === 'free' ? t('pricing.freePrice') : formatUsd(headlineUsd)}
                          </div>
                          {plan.tier !== 'free' && (
                            <div className={`pb-2 text-sm font-bold ${plan.popular ? 'text-white/[0.45]' : 'text-slate-400'}`}>
                              {t('pricing.perMonth')}
                            </div>
                          )}
                        </div>

                        {pricing && billingCycle === 'yearly' && (
                          <div className={`mt-1 text-xs font-semibold ${plan.popular ? 'text-white/[0.5]' : 'text-slate-500'}`}>
                            {t('pricing.billedYearly', { price: formatUsd(pricing.usd) })}
                          </div>
                        )}

                        {pricing && (
                          <div className={`mt-1 text-xs font-medium ${plan.popular ? 'text-white/[0.4]' : 'text-slate-400'}`}>
                            {t('pricing.checkoutThb', { price: formatThb(checkoutThb) })}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (plan.tier === 'free') {
                            handleGetStarted();
                          } else {
                            handleSelectPaidTier(plan.tier);
                          }
                        }}
                        className={`mt-6 w-full rounded-2xl px-5 py-3.5 text-sm font-black transition-all ${
                          plan.popular
                            ? 'bg-lime-300 text-growth-ink hover:bg-lime-200'
                            : 'bg-slate-950 text-white hover:bg-slate-800'
                        }`}
                      >
                        {plan.buttonText}
                      </button>

                      <ul className="mt-7 space-y-3">
                        {plan.features.map((feature) => (
                          <li key={feature.text} className="flex items-start gap-3">
                            <span className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full ${
                              feature.included
                                ? 'bg-lime-300 text-growth-ink'
                                : plan.popular ? 'bg-white/10 text-white/[0.26]' : 'bg-slate-100 text-slate-300'
                            }`}>
                              {feature.included && <Check className="h-3 w-3" />}
                            </span>
                            <span className={`text-sm font-semibold ${
                              feature.included
                                ? plan.popular ? 'text-white/75' : 'text-slate-600'
                                : plan.popular ? 'text-white/30' : 'text-slate-400'
                            }`}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </motion.article>
                  );
                })}
              </div>

              <p className="mt-8 text-center text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() => navigate('/upgrade')}
                  className="font-black text-slate-950 underline-offset-4 hover:underline"
                >
                  {t('pricing.addonLink', { defaultValue: 'ดูตารางเปรียบเทียบฟีเจอร์ทุกแพ็กเกจแบบละเอียด →' })}
                </button>
              </p>
            </div>
          </section>

          <section id="faq" data-snap className="bg-white py-20 lg:py-28">
            <div className="landing-section">
              <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-slate-500">
                    <Settings className="h-3.5 w-3.5" />
                    {t('faq.eyebrow')}
                  </div>
                  <h2 className="mt-5 text-4xl font-black tracking-[-0.04em] text-slate-950 md:text-5xl">
                    {t('faq.headline')}
                  </h2>
                </motion.div>

                <div className="space-y-4">
                  {faqItems.map((faq, index) => (
                    <motion.div
                      key={faq.question}
                      className="rounded-3xl border border-slate-200 bg-slate-50 p-6"
                      variants={fadeUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.2 }}
                      custom={index}
                    >
                      <h3 className="text-lg font-black text-slate-950">{faq.question}</h3>
                      <p className="mt-3 text-sm leading-7 text-slate-600">{faq.answer}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="cta" data-snap className="bg-white pb-20 lg:pb-28 pt-10">
            <div className="landing-section">
              <motion.div
                className="relative overflow-hidden rounded-[2.25rem] bg-growth-ink p-8 text-center text-white shadow-premium-card md:p-14"
                variants={scaleIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
              >
                <div className="landing-noise absolute inset-0 opacity-20" />
                <div className="absolute -left-16 -top-20 h-64 w-64 rounded-full bg-lime-300/[0.15] blur-[80px]" />
                <div className="absolute -bottom-20 -right-16 h-64 w-64 rounded-full bg-violet-500/20 blur-[80px]" />
                <div className="relative mx-auto max-w-3xl">
                  <div className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-white/10">
                    <Sparkles className="h-6 w-6 text-lime-200" />
                  </div>
                  <h2 className="text-4xl font-black tracking-[-0.045em] md:text-6xl">
                    {t('finalCta.headline')}
                  </h2>
                  <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-white/[0.62]">
                    {t('finalCta.subtitle')}
                  </p>
                  <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                    <button type="button" onClick={handleGetStarted} className="landing-primary-cta">
                      {t('finalCta.primary')}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => scrollTo('features')} className="landing-secondary-cta">
                      {t('finalCta.secondary')}
                    </button>
                  </div>
                  <p className="mt-5 text-sm font-semibold text-white/[0.38]">{t('finalCta.note')}</p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <footer className="relative z-10 border-t border-slate-200 bg-slate-50">
          <div className="landing-section py-12">
            <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr_0.8fr]">
              <div>
                <div className="mb-4 flex items-center gap-3">
                  {settings.logo_url ? (
                    <img
                      src={`${settings.logo_url}?v=${Date.now()}`}
                      alt={logoAlt}
                      width={32}
                      height={32}
                      /* Footer logo is well below the fold — defer it
                         entirely so it never competes with above-the-fold
                         resources for bandwidth. */
                      loading="lazy"
                      decoding="async"
                      className="h-8 w-auto object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <LogoMark compact />
                  )}
                  <span className="text-lg font-black text-slate-950">{brandName}</span>
                </div>
                <p className="max-w-md text-sm leading-7 text-slate-500">
                  {t('footer.description')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-black text-slate-950">{t('footer.navigation')}</h4>
                <ul className="mt-4 space-y-3">
                  {localizedNavItems.map((item) => (
                    <li key={item.id}>
                      <button type="button" onClick={() => scrollTo(item.id)} className="text-sm font-semibold text-slate-500 transition hover:text-slate-950">
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-black text-slate-950">{t('footer.legal')}</h4>
                <ul className="mt-4 space-y-3">
                  <li>
                    <button type="button" onClick={() => navigate('/terms')} className="text-sm font-semibold text-slate-500 transition hover:text-slate-950">
                      {t('footer.terms')}
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => navigate('/privacy')} className="text-sm font-semibold text-slate-500 transition hover:text-slate-950">
                      {t('footer.privacy')}
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => navigate('/legal')} className="text-sm font-semibold text-slate-500 transition hover:text-slate-950">
                      {t('footer.refund')}
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 text-sm font-semibold text-slate-400 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-0.5">
                <div>{t('footer.rights', { year: new Date().getFullYear(), brand: brandName })}</div>
                <div className="text-xs font-medium text-slate-400">
                  {t('footer.operator')}
                </div>
              </div>
              <span>{t('footer.tagline')}</span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};
